/**
 * `sync.studyGroups.group(id).drawSettings.*` — who may draw on the
 * group's PDF sources. Reads come straight from the store slice;
 * `update` is owner-only (enforced server-side in
 * `party/server/group/authorize.ts`).
 */

import {
  canDrawInGroup,
  type SGDrawSettings,
} from '@flashka/shared/sync-engine/schema/group-entities';

import { useStudyGroupStore } from '../internal/store/study-group-store';
import { nowISO, sendSG } from './helpers';
import type { SGScopedGroup } from './types';

export function createDrawSettingsApi(groupId: string): SGScopedGroup['drawSettings'] {
  return {
    use: () => useStudyGroupStore((s) => s.groups[groupId]?.drawSettings),
    get: () => useStudyGroupStore.getState().groups[groupId]?.drawSettings,
    update: (patch) => {
      const current = useStudyGroupStore.getState().groups[groupId]?.drawSettings;
      const now = nowISO();
      const next: SGDrawSettings = {
        mode: patch.mode ?? current?.mode ?? 'everyone',
        allowedUserIds: patch.allowedUserIds ?? current?.allowedUserIds ?? [],
        updatedAtISO: now,
      };
      useStudyGroupStore.getState().setDrawSettings(groupId, next);
      sendSG(groupId, {
        type: 'sg-drawsettings-push',
        sgDrawSettings: next,
        updatedAtISO: now,
      });
    },
    /** Reactive "may this user draw?" — group owner always may. */
    useCanDraw: (userId: string | undefined) =>
      useStudyGroupStore((s) => {
        if (!userId) return false;
        const slice = s.groups[groupId];
        const ownerId = slice?.meta?.createdBy ?? null;
        const isMember = !!slice?.members[userId];
        return canDrawInGroup(slice?.drawSettings, userId, ownerId, isMember);
      }),
  };
}
